<template>
  <div>
    <v-card-text class="pt-6">
      <div class="d-flex align-center mb-4">
        <div class="text-subtitle-1">Journal des exécutions (Logs)</div>
        <v-spacer></v-spacer>
        <v-btn icon small @click="fetchLogs" :loading="loading">
          <v-icon small>{{ icons.mdiRefresh }}</v-icon>
        </v-btn>
      </div>

      <!-- Filters -->
      <v-row dense class="mb-4">
        <v-col cols="12" sm="3">
          <v-text-field
            v-model="filters.context_code"
            label="Contexte"
            dense
            outlined
            hide-details
            clearable
            @input="debouncedFetch"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-model="filters.tool_step"
            label="Étape"
            dense
            outlined
            hide-details
            clearable
            @input="debouncedFetch"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="filters.response_status"
            :items="statusOptions"
            label="Statut"
            dense
            outlined
            hide-details
            clearable
            @change="fetchLogs"
          ></v-select>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="logs"
        :options.sync="options"
        :server-items-length="totalLogs"
        :loading="loading"
        class="elevation-0"
        @click:row="showDetail"
        show-expand
        single-expand
        :expanded.sync="expanded"
      >
        <template #[`item.created`]="{ item }">
          <span class="text-caption">{{ formatDate(item.created) }}</span>
        </template>

        <template #[`item.tool_step`]="{ item }">
          <v-chip v-if="item.tool_step" x-small label outlined>{{ item.tool_step }}</v-chip>
        </template>

        <template #[`item.response_status`]="{ item }">
          <v-chip
            x-small
            :color="getStatusColor(item.response_status)"
            dark
            label
          >
            {{ item.response_status }}
          </v-chip>
        </template>

        <template #[`item.duration_ms`]="{ item }">
          <span v-if="item.duration_ms" class="text-caption grey--text">
            {{ (item.duration_ms / 1000).toFixed(2) }}s
          </span>
        </template>

        <!-- Expanded details -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-4 grey lighten-5">
            <v-row v-if="logDetails[item.id]" dense>
              <v-col cols="12">
                <div class="d-flex align-center mb-2">
                  <div class="text-caption font-weight-bold mr-2 text-uppercase">Modèle:</div>
                  <v-chip x-small outlined color="primary">{{ logDetails[item.id].model_id }}</v-chip>
                  <v-spacer></v-spacer>
                  <div v-if="logDetails[item.id].error" class="text-caption error--text">
                    <v-icon x-small color="error" class="mr-1">{{ icons.mdiAlertCircle }}</v-icon>
                    {{ logDetails[item.id].error }}
                  </div>
                </div>
              </v-col>

              <v-col cols="6">
                <div class="text-overline mb-1 grey--text">System Prompt</div>
                <v-sheet border rounded class="pa-2 text-caption code-block-small mb-4">
                  {{ logDetails[item.id].system_prompt }}
                </v-sheet>

                <div class="text-overline mb-1 grey--text">User Prompt</div>
                <v-sheet border rounded class="pa-2 text-caption code-block-small">
                  {{ logDetails[item.id].user_prompt }}
                </v-sheet>
              </v-col>

              <v-col cols="6">
                <div class="text-overline mb-1 grey--text">Variables (JSON)</div>
                <v-sheet border rounded class="pa-2 text-caption code-block-small mb-4 overflow-auto" style="max-height: 200px">
                  <pre>{{ formatJson(logDetails[item.id].variables) }}</pre>
                </v-sheet>

                <div class="text-overline mb-1 grey--text">Réponse</div>
                <v-sheet border rounded class="pa-2 text-caption code-block-small overflow-auto" style="max-height: 300px">
                   {{ logDetails[item.id].response_text || '(Vide)' }}
                </v-sheet>
              </v-col>
            </v-row>
            <div v-else class="d-flex justify-center pa-4">
              <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-card-text>
  </div>
</template>

<script>
import { ref, reactive, watch, onMounted, set } from '@vue/composition-api'
import { mdiRefresh, mdiChevronDown, mdiChevronUp, mdiAlertCircle } from '@mdi/js'
import llprService from '@/services/llpr.service'

export default {
  name: 'PromptLogs',
  setup() {
    const loading = ref(false)
    const logs = ref([])
    const totalLogs = ref(0)
    const expanded = ref([])
    const logDetails = reactive({})
    
    const filters = reactive({
      context_code: '',
      tool_step: '',
      response_status: null
    })

    const options = ref({
      page: 1,
      itemsPerPage: 10,
      sortBy: ['created'],
      sortDesc: [true]
    })

    const statusOptions = ['pending', 'success', 'error', 'timeout']

    const icons = {
      mdiRefresh,
      mdiChevronDown,
      mdiChevronUp,
      mdiAlertCircle
    }

    const headers = [
      { text: '', value: 'data-table-expand' },
      { text: 'Date', value: 'created', width: '160px' },
      { text: 'Contexte', value: 'context_code' },
      { text: 'Étape', value: 'tool_step' },
      { text: 'Statut', value: 'response_status' },
      { text: 'Durée', value: 'duration_ms', align: 'end' },
    ]

    const fetchLogs = async () => {
      loading.value = true
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = options.value
        const params = {
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
          ...filters
        }
        const res = await llprService.getLogs(params)
        logs.value = res.data.items
        totalLogs.value = res.data.total
      } catch (error) {
        console.error('Failed to fetch logs:', error)
      } finally {
        loading.value = false
      }
    }

    let debounceTimer = null
    const debouncedFetch = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        options.value.page = 1
        fetchLogs()
      }, 500)
    }

    const showDetail = async (item) => {
      if (logDetails[item.id]) return
      
      try {
        const res = await llprService.getLogDetail(item.id)
        // Correcting reactivity for logDetails in Vue 2
        set(logDetails, item.id, res.data)
      } catch (error) {
        console.error('Failed to fetch log details:', error)
      }
    }

    // Watch for expansion changes
    watch(expanded, (newVal) => {
      if (newVal.length > 0) {
        showDetail(newVal[0])
      }
    })

    // Watch options change
    watch(options, () => {
      fetchLogs()
    }, { deep: true })

    const getStatusColor = (status) => {
      switch (status) {
        case 'success': return 'success'
        case 'error': return 'error'
        case 'pending': return 'warning'
        case 'timeout': return 'orange'
        default: return 'grey'
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString()
    }

    const formatJson = (val) => {
      if (!val) return '{}'
      try {
        return JSON.stringify(val, null, 2)
      } catch (e) {
        return val
      }
    }

    onMounted(() => {
      fetchLogs()
    })

    return {
      loading,
      logs,
      totalLogs,
      expanded,
      logDetails,
      filters,
      options,
      statusOptions,
      icons,
      headers,
      fetchLogs,
      debouncedFetch,
      showDetail,
      getStatusColor,
      formatDate,
      formatJson
    }
  }
}
</script>

<style scoped>
.code-block-small {
  background-color: #f5f5f5;
  padding: 8px;
  max-height: 400px;
  overflow-y: auto;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
  border: 1px solid #e0e0e0;
}
</style>
